const MAX_FAVORITES = 50;
const MAX_RECENTS = 8;

export interface WorkspaceNavigationPreferences {
  favoriteProjectIds: string[];
  recentProjectIds: string[];
}

const emptyPreferences = (): WorkspaceNavigationPreferences => ({
  favoriteProjectIds: [],
  recentProjectIds: [],
});

function preferenceKey(userId: string): string {
  return `pm:workspace-navigation:${userId}`;
}

function validIds(value: unknown, limit: number): string[] {
  if (!Array.isArray(value)) return [];
  return [
    ...new Set(value.filter((item): item is string => typeof item === 'string' && item.length > 0)),
  ].slice(0, limit);
}

export function readWorkspaceNavigation(userId?: string): WorkspaceNavigationPreferences {
  if (!userId || typeof window === 'undefined') return emptyPreferences();

  try {
    const parsed = JSON.parse(window.localStorage.getItem(preferenceKey(userId)) ?? '{}') as {
      favoriteProjectIds?: unknown;
      recentProjectIds?: unknown;
    };
    return {
      favoriteProjectIds: validIds(parsed.favoriteProjectIds, MAX_FAVORITES),
      recentProjectIds: validIds(parsed.recentProjectIds, MAX_RECENTS),
    };
  } catch {
    return emptyPreferences();
  }
}

export function writeWorkspaceNavigation(
  userId: string,
  preferences: WorkspaceNavigationPreferences,
): void {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(
    preferenceKey(userId),
    JSON.stringify({
      favoriteProjectIds: validIds(preferences.favoriteProjectIds, MAX_FAVORITES),
      recentProjectIds: validIds(preferences.recentProjectIds, MAX_RECENTS),
    }),
  );
}

export function toggleFavoriteProject(
  preferences: WorkspaceNavigationPreferences,
  projectId: string,
): WorkspaceNavigationPreferences {
  const isFavorite = preferences.favoriteProjectIds.includes(projectId);
  return {
    ...preferences,
    favoriteProjectIds: isFavorite
      ? preferences.favoriteProjectIds.filter((id) => id !== projectId)
      : [projectId, ...preferences.favoriteProjectIds].slice(0, MAX_FAVORITES),
  };
}

export function recordRecentProject(
  preferences: WorkspaceNavigationPreferences,
  projectId: string,
): WorkspaceNavigationPreferences {
  return {
    ...preferences,
    recentProjectIds: [
      projectId,
      ...preferences.recentProjectIds.filter((id) => id !== projectId),
    ].slice(0, MAX_RECENTS),
  };
}
